import type { CSSProperties } from 'react';
import type { ThemeDNA, ThemeColorProfile, DesignTokens, UserCustomization, UIStyle } from '@commonqr/shared/config/themeEngine';
import { CSS_VAR_MAP } from '@commonqr/shared/config/themeEngine';

function hexToLinearRGB(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const linearize = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return [linearize(r), linearize(g), linearize(b)];
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToLinearRGB(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(fg: string, bg: string): number {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker  = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function isWCAG_AA(fg: string, bg: string): boolean {
  return contrastRatio(fg, bg) >= 4.5;
}

function autoTextColor(bg: string): string {
  return relativeLuminance(bg) > 0.179 ? '#000000' : '#ffffff';
}

function adjustBrightness(hex: string, step: number, direction: 'darken' | 'lighten'): string {
  const [r, g, b] = hexToLinearRGB(hex);
  const toSRGB = (c: number) => Math.round(Math.min(255, Math.max(0, (c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055) * 255)));
  const sr = toSRGB(r); const sg = toSRGB(g); const sb = toSRGB(b);
  const delta = Math.round(255 * step);
  const adjust = direction === 'lighten' ? delta : -delta;
  const clamp = (v: number) => Math.min(255, Math.max(0, v + adjust));
  const toHex = (v: number) => clamp(v).toString(16).padStart(2, '0');
  return `#${toHex(sr)}${toHex(sg)}${toHex(sb)}`;
}

const DENSITY_SCALE: Record<ThemeDNA['density'], { profileName: number; category: number; description: number; buttonLabel: number; metadata: number }> = {
  compact:     { profileName: 20, category: 13, description: 12, buttonLabel: 12, metadata: 11 },
  comfortable: { profileName: 22, category: 14, description: 13, buttonLabel: 13, metadata: 12 },
  spacious:    { profileName: 26, category: 15, description: 14, buttonLabel: 14, metadata: 12 },
};

const FONT_SIZE_MULTIPLIER: Record<NonNullable<UserCustomization['fontSize']>, number> = {
  auto: 1.00, sm: 0.88, md: 1.00, lg: 1.15,
};

const MOOD_HEADING_WEIGHT: Record<ThemeDNA['mood'], number> = {
  corporate: 700, luxury: 600, nature: 500, cyberpunk: 700,
  playful: 700, organic: 500, minimal: 600, cultural: 600,
};

const UI_STYLE_RADIUS: Record<UIStyle, number> = {
  minimal: 0, solid: 8, outline: 8, glass: 12, soft: 10,
  pill: 9999, elevated: 8, neon: 4, elegant: 2, futuristic: 0,
};

const UI_STYLE_SHADOW: Record<UIStyle, string> = {
  minimal: 'none', solid: 'none', outline: 'none',
  glass: '0 4px 16px rgba(0,0,0,0.15)', soft: 'none', pill: 'none',
  elevated: '0 4px 12px rgba(0,0,0,0.25)', neon: 'none',
  elegant: 'none', futuristic: '0 0 8px rgba(0,0,0,0.5)',
};

const UI_STYLE_BLUR: Record<UIStyle, number> = {
  minimal: 0, solid: 0, outline: 0, glass: 10, soft: 0,
  pill: 0, elevated: 0, neon: 4, elegant: 0, futuristic: 0,
};

const UI_STYLE_OPACITY: Record<UIStyle, number> = {
  minimal: 0, solid: 1, outline: 0, glass: 0.10, soft: 0.20,
  pill: 1, elevated: 1, neon: 0, elegant: 0, futuristic: 0.85,
};

export function resolveTokens(
  themeDNA: ThemeDNA,
  colorProfile: ThemeColorProfile,
  userCustomization: UserCustomization,
): DesignTokens {
  let uiStyle: UIStyle = themeDNA.recommendedUI;
  const density = DENSITY_SCALE[themeDNA.density];
  let colorText       = colorProfile.textColor;
  let colorButton     = colorProfile.buttonColor;
  let fontHeading     = `'${themeDNA.headingFont}', serif`;
  let fontBody        = `'${themeDNA.bodyFont}', sans-serif`;
  let fontButton      = `'${themeDNA.buttonFont}', sans-serif`;
  let buttonStyle: UIStyle = uiStyle;

  const colorTextMuted = adjustBrightness(colorText, 0.3, relativeLuminance(colorText) > 0.5 ? 'darken' : 'lighten');
  const colorBorder = colorText === '#ffffff' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.20)';
  const overlayAlpha = Math.round(colorProfile.overlayOpacity * 100) / 100;
  const colorOverlay = `rgba(0,0,0,${overlayAlpha})`;
  let colorButtonText = autoTextColor(colorButton);

  if (userCustomization.uiStyle)     { buttonStyle = userCustomization.uiStyle; }
  if (userCustomization.buttonStyle) { buttonStyle = userCustomization.buttonStyle; }
  let buttonRadius  = userCustomization.buttonRadius ?? UI_STYLE_RADIUS[buttonStyle];
  const buttonBlur    = UI_STYLE_BLUR[buttonStyle];
  const buttonOpacity = UI_STYLE_OPACITY[buttonStyle];

  if (userCustomization.fontFamily) {
    const fam = userCustomization.fontFamily;
    fontHeading = `'${fam}', sans-serif`;
    fontBody    = `'${fam}', sans-serif`;
    fontButton  = `'${fam}', sans-serif`;
  }

  const fsMultiplier = userCustomization.fontSize ? FONT_SIZE_MULTIPLIER[userCustomization.fontSize] : 1.0;

  if (userCustomization.textColor) {
    const c = userCustomization.textColor;
    if (isWCAG_AA(c, '#000000') || isWCAG_AA(c, '#ffffff')) colorText = c;
  }
  if (userCustomization.buttonColor) {
    const c = userCustomization.buttonColor;
    const ct = autoTextColor(c);
    if (isWCAG_AA(ct, c)) { colorButton = c; colorButtonText = ct; }
  }

  let buttonShadow = UI_STYLE_SHADOW[buttonStyle];
  if (buttonStyle === 'neon') buttonShadow = `0 0 12px ${colorButton}`;

  const weightHeading = MOOD_HEADING_WEIGHT[themeDNA.mood];
  const weightButton  = weightHeading >= 700 ? 600 : 500;
  const round = (n: number) => Math.round(n);

  return {
    colorText, colorTextMuted, colorButton, colorButtonText, colorBorder, colorOverlay,
    fontHeading, fontBody, fontButton,
    sizeProfileName: round(density.profileName * fsMultiplier),
    sizeCategory:    round(density.category    * fsMultiplier),
    sizeDescription: round(density.description * fsMultiplier),
    sizeButtonLabel: round(density.buttonLabel * fsMultiplier),
    sizeMetadata:    round(density.metadata    * fsMultiplier),
    weightHeading, weightButton,
    buttonStyle, buttonRadius, buttonShadow, buttonBlur, buttonOpacity,
  };
}

export function injectTokens(el: HTMLElement, tokens: DesignTokens): void {
  const s = el.style;
  s.setProperty(CSS_VAR_MAP.colorText,       tokens.colorText);
  s.setProperty(CSS_VAR_MAP.colorTextMuted,  tokens.colorTextMuted);
  s.setProperty(CSS_VAR_MAP.colorButton,     tokens.colorButton);
  s.setProperty(CSS_VAR_MAP.colorButtonText, tokens.colorButtonText);
  s.setProperty(CSS_VAR_MAP.colorBorder,     tokens.colorBorder);
  s.setProperty(CSS_VAR_MAP.colorOverlay,    tokens.colorOverlay);
  s.setProperty(CSS_VAR_MAP.fontHeading,     tokens.fontHeading);
  s.setProperty(CSS_VAR_MAP.fontBody,        tokens.fontBody);
  s.setProperty(CSS_VAR_MAP.fontButton,      tokens.fontButton);
  s.setProperty(CSS_VAR_MAP.sizeProfileName, `${tokens.sizeProfileName}px`);
  s.setProperty(CSS_VAR_MAP.sizeCategory,    `${tokens.sizeCategory}px`);
  s.setProperty(CSS_VAR_MAP.sizeDescription, `${tokens.sizeDescription}px`);
  s.setProperty(CSS_VAR_MAP.sizeButtonLabel, `${tokens.sizeButtonLabel}px`);
  s.setProperty(CSS_VAR_MAP.sizeMetadata,    `${tokens.sizeMetadata}px`);
  s.setProperty(CSS_VAR_MAP.weightHeading,   String(tokens.weightHeading));
  s.setProperty(CSS_VAR_MAP.weightButton,    String(tokens.weightButton));
  s.setProperty(CSS_VAR_MAP.buttonStyle,     tokens.buttonStyle);
  s.setProperty(CSS_VAR_MAP.buttonRadius,    `${tokens.buttonRadius}px`);
  s.setProperty(CSS_VAR_MAP.buttonShadow,    tokens.buttonShadow);
  s.setProperty(CSS_VAR_MAP.buttonBlur,      `${tokens.buttonBlur}px`);
  s.setProperty(CSS_VAR_MAP.buttonOpacity,   String(tokens.buttonOpacity));
}

export function clearTokens(el: HTMLElement): void {
  Object.values(CSS_VAR_MAP).forEach(v => el.style.removeProperty(v));
}

export function tokensToStyle(tokens: DesignTokens): CSSProperties {
  return {
    [CSS_VAR_MAP.colorText]:       tokens.colorText,
    [CSS_VAR_MAP.colorTextMuted]:  tokens.colorTextMuted,
    [CSS_VAR_MAP.colorButton]:     tokens.colorButton,
    [CSS_VAR_MAP.colorButtonText]: tokens.colorButtonText,
    [CSS_VAR_MAP.colorBorder]:     tokens.colorBorder,
    [CSS_VAR_MAP.colorOverlay]:    tokens.colorOverlay,
    [CSS_VAR_MAP.fontHeading]:     tokens.fontHeading,
    [CSS_VAR_MAP.fontBody]:        tokens.fontBody,
    [CSS_VAR_MAP.fontButton]:      tokens.fontButton,
    [CSS_VAR_MAP.sizeProfileName]: `${tokens.sizeProfileName}px`,
    [CSS_VAR_MAP.sizeCategory]:    `${tokens.sizeCategory}px`,
    [CSS_VAR_MAP.sizeDescription]: `${tokens.sizeDescription}px`,
    [CSS_VAR_MAP.sizeButtonLabel]: `${tokens.sizeButtonLabel}px`,
    [CSS_VAR_MAP.sizeMetadata]:    `${tokens.sizeMetadata}px`,
    [CSS_VAR_MAP.weightHeading]:   String(tokens.weightHeading),
    [CSS_VAR_MAP.weightButton]:    String(tokens.weightButton),
    [CSS_VAR_MAP.buttonStyle]:     tokens.buttonStyle,
    [CSS_VAR_MAP.buttonRadius]:    `${tokens.buttonRadius}px`,
    [CSS_VAR_MAP.buttonShadow]:    tokens.buttonShadow,
    [CSS_VAR_MAP.buttonBlur]:      `${tokens.buttonBlur}px`,
    [CSS_VAR_MAP.buttonOpacity]:   String(tokens.buttonOpacity),
  } as CSSProperties;
}

export type { DesignTokens, ThemeDNA, ThemeColorProfile, UserCustomization, UIStyle };
